import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ShareNetwork, Copy, Check, Warning, LockKey, ArrowsClockwise } from '@phosphor-icons/react';
import { shareApi, withHonorific } from './shareApi';

const DAYS = [1, 3, 7, 14, 30];
const WEEK = ['日', '月', '火', '水', '木', '金', '土'];

const jpDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${WEEK[d.getDay()]}）`;
};

/** そのままメールに貼れる本文。パスワードは絶対に入れない。 */
const buildMessage = (made, url) => {
  const docs = made.documents || [];
  const names = docs.map((d) => d.title || d.filename || d.document_number).filter(Boolean);
  const shown = names.slice(0, 5).join('、');
  const rest = names.length > 5 ? `${shown} ほか${names.length - 5}件` : shown;
  const sender = made.created_by || '';
  const lines = [
    withHonorific(made.recipient),
    '',
    'いつもお世話になっております。',
    `株式会社センスビーの${sender}です。`,
    '',
    '下記リンクにて書類を共有いたします。',
    '',
    '■ 共有リンク',
    url,
    `■ 有効期限：${jpDate(made.expires_at)}まで`,
    `■ 内容：${docs.length}件（${rest}）`,
  ];
  if ((made.note || '').trim()) lines.push(`■ メモ：${made.note.trim()}`);
  lines.push('', '閲覧にはパスワードが必要です。パスワードは別途お知らせいたします。',
    '', 'よろしくお願いいたします。', sender);
  return lines.join('\n');
};

const CopyRow = ({ label, value, testid }) => {
  const [done, setDone] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
      <p className="text-[10px] text-zinc-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate text-[12px] font-mono text-zinc-900"
          data-testid={`${testid}-value`}>{value}</code>
        <button type="button" data-testid={testid}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setDone(true); setTimeout(() => setDone(false), 1500);
              toast.success('コピーしました');
            } catch { toast.error('コピーできませんでした'); }
          }}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 bg-white text-[10px] text-zinc-700 hover:border-teal-500">
          {done ? <Check size={11} className="text-teal-600" /> : <Copy size={11} />}コピー
        </button>
      </div>
    </div>
  );
};

/** 外部共有をつくるダイアログ。作成後はリンクとパスワードを別々に表示する。 */
export default function ShareDialog({ open, onClose, documentIds = [], bundleId = null,
  label = '', isAdmin = false, onCreated }) {
  const [recipient, setRecipient] = useState('');
  const [note, setNote] = useState('');
  const [days, setDays] = useState(7);
  const [allowDownload, setAllowDownload] = useState(true);
  const [maxDownloads, setMaxDownloads] = useState('');
  const [password, setPassword] = useState('');
  const [allowConfidential, setAllowConfidential] = useState(false);
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const [msg, setMsg] = useState('');
  const [mailPaused, setMailPaused] = useState(false);
  const [pwCopied, setPwCopied] = useState(false);

  // 初期パスワードはサーバーの生成器（紛らわしい字を除く10文字）をそのまま使う。
  const regenerate = useCallback(async () => {
    try {
      const r = await shareApi.newPassword();
      setPassword(r.password);
      setMailPaused(!!r.paused);
    } catch { /* 取れなければ空欄のまま（作成時にサーバー側で自動生成） */ }
  }, []);

  useEffect(() => {
    if (open) { setPwCopied(false); regenerate(); }
  }, [open, regenerate]);

  if (!open) return null;
  const count = bundleId ? null : documentIds.length;
  const origin = window.location.origin;

  const submit = async () => {
    if (!recipient.trim()) { toast.error('相手先を入れてください'); return; }
    if (password && password.length < 8) { toast.error('パスワードは8文字以上にしてください'); return; }
    setBusy(true);
    try {
      const r = await shareApi.create({
        document_ids: documentIds, bundle_id: bundleId, recipient: recipient.trim(),
        note: note.trim(), days: Number(days), allow_download: allowDownload,
        max_downloads: maxDownloads ? Number(maxDownloads) : null,
        password: password || null, allow_confidential: allowConfidential,
      });
      setMade(r);
      setMsg(buildMessage(r, `${window.location.origin}${r.url_path}`));
      onCreated?.(r);
      toast.success('共有リンクを作りました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '共有できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4"
      data-testid="dms-share-dialog" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <ShareNetwork size={18} weight="duotone" className="text-teal-700" />
          <h2 className="text-base font-bold text-zinc-900">外部共有</h2>
          <span className="text-[11px] text-zinc-400">
            {bundleId ? `バインダー${label ? ` — ${label}` : ''}` : `${count}件の書類`}
          </span>
        </div>

        {!made ? (
          <>
            <label className="block">
              <span className="text-[11px] text-zinc-600">相手先（会社名・氏名）<span className="text-rose-600">*</span></span>
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)} autoFocus
                data-testid="share-recipient" placeholder="例: ナブテスコ株式会社 山田様"
                className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] outline-none focus:border-teal-500" />
            </label>
            <label className="block">
              <span className="text-[11px] text-zinc-600">メモ（相手にも表示されます）</span>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="share-note" placeholder="例: 10/20 打合せ資料"
                className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] outline-none focus:border-teal-500" />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11px] text-zinc-600">有効期限</span>
                <select value={days} onChange={(e) => setDays(e.target.value)} data-testid="share-days"
                  className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] bg-white">
                  {DAYS.map((d) => <option key={d} value={d}>{d}日</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] text-zinc-600">ダウンロード回数の上限（任意）</span>
                <input value={maxDownloads} onChange={(e) => setMaxDownloads(e.target.value.replace(/\D/g, ''))}
                  data-testid="share-max-downloads" placeholder="制限なし"
                  className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] outline-none focus:border-teal-500" />
                <span className="mt-1 block text-[10px] text-zinc-400 leading-relaxed"
                  data-testid="share-max-downloads-hint">
                  この回数ダウンロードされるとリンク全体が無効になります（ZIPは何件入っていても1回。
                  画面で見るだけは回数に含みません。空欄なら制限なし）
                </span>
              </label>
            </div>

            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-zinc-600">許可:</span>
              <button type="button" onClick={() => setAllowDownload(false)} data-testid="share-view-only"
                className={`px-2 py-1 rounded border ${!allowDownload ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>閲覧のみ</button>
              <button type="button" onClick={() => setAllowDownload(true)} data-testid="share-allow-download"
                className={`px-2 py-1 rounded border ${allowDownload ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>ダウンロード可</button>
            </div>

            <label className="block">
              <span className="text-[11px] text-zinc-600 inline-flex items-center gap-1">
                <LockKey size={12} />パスワード（自動生成・変更可）
              </span>
              <div className="mt-1 flex items-center gap-1.5">
                <input value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="share-password" placeholder="8文字以上"
                  className="flex-1 min-w-0 border border-zinc-300 rounded px-2 py-1.5 font-mono text-[13px] outline-none focus:border-teal-500" />
                <button type="button" data-testid="share-password-regenerate" onClick={regenerate}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded border border-zinc-300 bg-white text-[10px] text-zinc-700 hover:border-teal-500">
                  <ArrowsClockwise size={11} />再生成
                </button>
                <button type="button" data-testid="share-password-copy"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(password);
                      setPwCopied(true); setTimeout(() => setPwCopied(false), 1500);
                      toast.success('コピーしました');
                    } catch { toast.error('コピーできませんでした'); }
                  }}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1.5 rounded border border-zinc-300 bg-white text-[10px] text-zinc-700 hover:border-teal-500">
                  {pwCopied ? <Check size={11} className="text-teal-600" /> : <Copy size={11} />}コピー
                </button>
              </div>
            </label>

            {isAdmin && (
              <label className="flex items-start gap-2 text-[11px] text-zinc-600">
                <input type="checkbox" checked={allowConfidential} data-testid="share-allow-confidential"
                  onChange={(e) => setAllowConfidential(e.target.checked)} className="mt-0.5 accent-rose-600" />
                <span>社外秘・制限付きの書類を含めて共有する（管理者のみ・監査に残ります）</span>
              </label>
            )}

            <p className="text-[10px] text-zinc-400 leading-relaxed" data-testid="share-footer-note">
              共有するのは今のファイル（版）です。あとで差し替えても、相手が見るものは変わりません。
              {mailPaused
                ? 'メール送信は停止中のため、リンクとパスワードはこの画面からコピーして渡してください。'
                : 'リンクとパスワードは別々の方法（例: リンクはメール、パスワードは電話・FAX）で相手に伝えてください。'}
            </p>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} data-testid="share-cancel"
                className="px-3 py-1.5 rounded border border-zinc-300 text-[12px] text-zinc-600">やめる</button>
              <button type="button" onClick={submit} disabled={busy} data-testid="share-submit"
                className="px-3 py-1.5 rounded bg-teal-700 text-white text-[12px] font-semibold disabled:opacity-40">
                {busy ? '作成中…' : '共有リンクを作る'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
              <Warning size={14} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
              パスワードはこの画面でしか見られません。閉じる前にコピーしてください。
              リンクはメール、パスワードは電話やFAXなど別の方法で伝えると安全です。
            </div>
            <CopyRow label="共有リンク" testid="share-copy-link" value={`${origin}${made.url_path}`} />
            <CopyRow label="パスワード" testid="share-copy-password" value={made.password} />
            <CopyRow label="パスワード連絡用（電話・FAX・別メール）" testid="share-copy-password-text"
              value={`先ほどお送りした共有リンクのパスワードは ${made.password} です。`} />
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] text-zinc-500">そのまま貼れる文面（直せます）</p>
                <button type="button" data-testid="share-copy-text"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(msg);
                      toast.success('コピーしました');
                    } catch { toast.error('コピーできませんでした'); }
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-300 bg-white text-[10px] text-zinc-700 hover:border-teal-500">
                  <Copy size={11} />コピー
                </button>
              </div>
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)}
                data-testid="share-message" rows={Math.min(24, msg.split('\n').length + 1)}
                className="w-full resize-y bg-white border border-zinc-300 rounded px-2 py-1.5 text-[12px] leading-relaxed text-zinc-900 outline-none focus:border-teal-500" />
            </div>
            <p className="text-[10px] text-zinc-400">
              有効期限 {String(made.expires_at).slice(0, 10)} ・ {made.documents?.length || 0}件 ・
              {made.allow_download ? 'ダウンロード可' : '閲覧のみ'}
              {made.max_downloads ? ` ・ 上限${made.max_downloads}回` : ''}
            </p>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} data-testid="share-done"
                className="px-3 py-1.5 rounded bg-teal-700 text-white text-[12px] font-semibold">閉じる</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
